import React, { useEffect, useState } from 'react';
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon,
    IonToast,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
} from '@ionic/react';
import { closeOutline, addCircleOutline, trashOutline } from 'ionicons/icons';
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Divider,
    FormControl,
    Select,
    InputLabel,
    InputAdornment,
    IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Procedure } from '../../../models/procedure';
import { VendorProcedure, FeeType } from '../../../models/vendor';
// import { ProcedureService } from '../../../api/procedure.service';
import * as ProcedureService from '../../../api/procedure.service';
// import { v4 as uuidv4 } from 'uuid'; // Assuming uuid is available or use simple random string

interface VendorProcedureModalProps {
    isOpen: boolean;
    onClose: () => void;
    vendorId: string;
    onSave: (procedures: VendorProcedure[]) => void;
}

interface NewAssignment {
    id: string; // temp id for list management
    procedureId: string;
    agreedFee: string;
    agreedFeeType: FeeType;
    professionalFee: string;
    professionalFeeType: FeeType;
    effectiveFrom: Dayjs | null;
    effectiveTo: Dayjs | null;
}

const VendorProcedureModal: React.FC<VendorProcedureModalProps> = ({
    isOpen,
    onClose,
    vendorId,
    onSave
}) => {
    const [availableProcedures, setAvailableProcedures] = useState<Procedure[]>([]);
    const [assignments, setAssignments] = useState<NewAssignment[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Initial load of procedures
    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            ProcedureService.getProcedures()
                .then(res => {
                    // const 
                    const data = res.data || [];
                    console.log('ftected rpodcere', res)
                    setAvailableProcedures(data);
                    // Add one empty row by default if list is empty
                    if (assignments.length === 0) {
                        addNewRow();
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        }
    }, [isOpen]);

    const addNewRow = () => {
        setAssignments([
            ...assignments,
            {
                id: Math.random().toString(36).substr(2, 9),
                procedureId: '',
                agreedFee: '0',
                agreedFeeType: 'FIXED',
                professionalFee: '0',
                professionalFeeType: 'FIXED',
                effectiveFrom: dayjs(),
                effectiveTo: dayjs().add(1, 'year')
            }
        ]);
    };

    const removeRow = (id: string) => {
        if (assignments.length > 1) {
            setAssignments(assignments.filter(a => a.id !== id));
        }
    };

    const updateRow = (id: string, field: keyof NewAssignment, value: any) => {
        setAssignments(prev => prev.map(a =>
            a.id === id ? { ...a, [field]: value } : a
        ));
    };

    const handleSave = () => {
        // Validation
        const isValid = assignments.every(a =>
            a.procedureId &&
            a.effectiveFrom &&
            a.effectiveTo &&
            !isNaN(parseFloat(a.agreedFee)) &&
            !isNaN(parseFloat(a.professionalFee))
        );

        if (!isValid) {
            setError("Please fill all required fields correctly.");
            return;
        }

        const formattedProcedures: VendorProcedure[] = assignments.map(a => ({
            vendor_id: vendorId,
            procedure_id: a.procedureId,
            agreed_fee: parseFloat(a.agreedFee),
            agreed_fee_type: a.agreedFeeType,
            professional_fee: parseFloat(a.professionalFee),
            professional_fee_type: a.professionalFeeType,
            effective_from: a.effectiveFrom!.toISOString(),
            effective_to: a.effectiveTo!.toISOString()
        }));

        onSave(formattedProcedures);
        onClose();
        setAssignments([]); // Reset
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose} style={{ '--width': '95%', '--height': '95%', '--max-width': '1200px' }}>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonTitle>Assign Procedures</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={onClose}>
                            <IonIcon icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Select procedures and define the fee structure for this vendor. You can add multiple procedures at once.
                    </Typography>

                    {assignments.map((assignment, index) => (
                        <Box key={assignment.id} sx={{ mb: 3, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Assignment #{index + 1}
                                </Typography>
                                {assignments.length > 1 && (
                                    <IconButton size="small" color="error" onClick={() => removeRow(assignment.id)}>
                                        <IonIcon icon={trashOutline} />
                                    </IconButton>
                                )}
                            </Box>

                            <Grid container spacing={2}>
                                {/* Procedure Selector */}
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Procedure</InputLabel>
                                        <Select
                                            value={assignment.procedureId}
                                            label="Procedure"
                                            onChange={(e) => updateRow(assignment.id, 'procedureId', e.target.value)}
                                        >
                                            {availableProcedures.map(p => (
                                                <MenuItem key={p.geacloud_procedures_id} value={p.geacloud_procedures_id}>{p.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Fees Row */}
                                <Grid item xs={12} md={8}>
                                    <Grid container spacing={2}>
                                        {/* Agreed Fee */}
                                        <Grid item xs={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Agreed Fee"
                                                type="number"
                                                value={assignment.agreedFee}
                                                onChange={(e) => updateRow(assignment.id, 'agreedFee', e.target.value)}
                                                InputProps={{
                                                    startAdornment: assignment.agreedFeeType === 'FIXED' ? <InputAdornment position="start">₦</InputAdornment> : null,
                                                    endAdornment: assignment.agreedFeeType === 'PERCENTAGE' ? <InputAdornment position="end">%</InputAdornment> : null,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={assignment.agreedFeeType}
                                                    label="Type"
                                                    onChange={(e) => updateRow(assignment.id, 'agreedFeeType', e.target.value)}
                                                >
                                                    <MenuItem value="FIXED">Fixed (₦)</MenuItem>
                                                    <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Professional Fee */}
                                        <Grid item xs={6} md={3}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Professional Fee"
                                                type="number"
                                                value={assignment.professionalFee}
                                                onChange={(e) => updateRow(assignment.id, 'professionalFee', e.target.value)}
                                                InputProps={{
                                                    startAdornment: assignment.professionalFeeType === 'FIXED' ? <InputAdornment position="start">₦</InputAdornment> : null,
                                                    endAdornment: assignment.professionalFeeType === 'PERCENTAGE' ? <InputAdornment position="end">%</InputAdornment> : null,
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={assignment.professionalFeeType}
                                                    label="Type"
                                                    onChange={(e) => updateRow(assignment.id, 'professionalFeeType', e.target.value)}
                                                >
                                                    <MenuItem value="FIXED">Fixed (₦)</MenuItem>
                                                    <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                {/* Dates */}
                                <Grid item xs={12} md={8}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <DatePicker
                                                label="Effective From"
                                                value={assignment.effectiveFrom}
                                                onChange={(date) => updateRow(assignment.id, 'effectiveFrom', date)}
                                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <DatePicker
                                                label="Effective To"
                                                value={assignment.effectiveTo}
                                                onChange={(date) => updateRow(assignment.id, 'effectiveTo', date)}
                                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    ))}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <IonButton fill="outline" onClick={addNewRow}>
                            <IonIcon icon={addCircleOutline} slot="start" />
                            Add Another Procedure
                        </IonButton>
                    </Box>

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <IonButton color="medium" fill="clear" onClick={onClose}>Cancel</IonButton>
                        <IonButton onClick={handleSave}>Save Assignments</IonButton>
                    </Box>
                </Box>
            </IonContent>

            <IonToast
                isOpen={!!error}
                message={error || ""}
                duration={3000}
                onDidDismiss={() => setError(null)}
                color="danger"
            />
        </IonModal>
    );
};

export default VendorProcedureModal;
